import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Lock } from 'lucide-react';
import { supabase } from '../utils/supabase';

const DiscountContainer = styled.div`
  margin: 0.75rem 0.75rem 1.5rem;
  padding: 1rem 1.25rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Title = styled.span`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-800);
`;

const DiscountForm = styled.form`
  display: flex;
  gap: 0.75rem;
  align-items: center;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid ${props => props.$error ? '#fca5a5' : '#dbe4ef'};
  font-size: var(--text-sm);
  font-weight: 500;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.$error ? '#f87171' : '#0f172a'};
    box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.12);
  }

  &::placeholder {
    color: var(--gray-400);
    font-weight: 400;
  }

  ${props => props.readOnly && `
    background-color: #f1f5f9;
    color: var(--gray-600);
    cursor: not-allowed;
    border-color: #cbd5e1;

    &:focus {
      border-color: #cbd5e1;
      box-shadow: none;
    }
  `}
`;

const ApplyButton = styled.button`
  padding: 0.7rem 1.5rem;
  border-radius: 8px;
  border: none;
  background: var(--secondary-color);
  color: white;
  font-weight: 600;
  font-size: var(--text-sm);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(15, 29, 54, 0.18);
    background: #0d1527;
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ClearButton = styled.button`
  align-self: flex-start;
  background: transparent;
  border: none;
  color: var(--gray-500);
  font-size: var(--text-xs);
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    color: #dc2626;
    text-decoration: underline;
  }
`;

const Feedback = styled.div`
  font-size: var(--text-xs);
  font-weight: 500;
  color: ${props => {
    if (props.$type === 'error') return '#dc2626';
    if (props.$type === 'success') return '#0f766e';
    return 'var(--gray-500)';
  }};
`;

const DiscountSummary = styled.div`
  font-size: var(--text-xs);
  color: var(--gray-600);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.25rem;
  border-top: 1px solid #f1f5f9;
`;

const LockIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-xs);
  color: var(--gray-500);
  font-weight: 500;
  margin-top: -0.25rem;
`;

const LockIcon = styled(Lock)`
  width: 14px;
  height: 14px;
  color: var(--gray-400);
`;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);

const resolveDiscountPercentage = (code) => {
  if (!code) return null;

  const normalized = code.trim().toUpperCase();
  if (!normalized) return null;

  const codeMap = {
    'KFAIR42781': 10, // K-Fair 2025 discount code - 10% off
  };

  if (codeMap[normalized] !== undefined) {
    return codeMap[normalized];
  }

  const numericMatch = normalized.match(/(\d+(\.\d+)?)/);
  if (!numericMatch) return null;

  const value = parseFloat(numericMatch[1]);
  if (Number.isNaN(value)) return null;

  return Math.min(Math.max(value, 0), 100);
};

// Check if user already used their discount code (prevents duplicate emails)
const checkDiscountStatus = async (userEmail) => {
  const email = userEmail || localStorage.getItem('indus_user_email');

  console.log('🔍 [Discount Check] Checking if code already used...');
  console.log('🔍 [Discount Check] Email:', email);

  if (!email) {
    console.log('❌ [Discount Check] No email found');
    return { already_used: false, error: 'Please sign up first to use this discount code' };
  }

  try {
    // Call edge function to check if code was already used
    const { data, error } = await supabase.functions.invoke('check-discount-status', {
      body: { email }
    });

    console.log('🔍 [Discount Check] Response:', { data, error });

    if (error) {
      console.error('❌ [Discount Check] Error:', error);
      return { already_used: false, error: 'Unable to check discount status' };
    }

    return {
      already_used: data?.already_used || false,
      discount_percentage: data?.discount_percentage || 10,
      success: true
    };

  } catch (err) {
    console.error('❌ [Discount Check] Unexpected error:', err);
    return { already_used: false, error: 'An unexpected error occurred' };
  }
};

// Validate discount code via secure edge function (only called if code not yet used)
const validateAndApplyDiscountCode = async (code, userEmail) => {
  const normalized = code.trim().toUpperCase();

  console.log('🔍 [Discount Validation] Starting validation...');
  console.log('🔍 [Discount Validation] Code:', normalized);

  // Get user's email from localStorage if not provided
  const email = userEmail || localStorage.getItem('indus_user_email');

  console.log('🔍 [Discount Validation] Email from localStorage:', email);

  if (!email) {
    console.log('❌ [Discount Validation] No email found in localStorage');
    return { success: false, error: 'Please sign up first to use this discount code' };
  }

  try {
    // Call the secure edge function that handles all validation and database updates
    console.log('🔍 [Discount Validation] Calling edge function...');

    const { data, error } = await supabase.functions.invoke('validate-discount-code', {
      body: { email, code }
    });

    console.log('🔍 [Discount Validation] Edge function response:', { data, error });

    if (error) {
      console.error('❌ [Discount Validation] Edge function error:', error);
      return {
        success: false,
        error: 'Unable to validate discount code. Please try again.'
      };
    }

    if (!data || !data.success) {
      console.log('❌ [Discount Validation] Validation failed:', data?.error);
      return {
        success: false,
        error: data?.error || 'Failed to validate discount code'
      };
    }

    // Success!
    console.log('✅ [Discount Validation] Validation successful!');
    return {
      success: true,
      discount_percentage: data.discount_percentage,
      message: data.message
    };

  } catch (err) {
    console.error('❌ [Discount Validation] Unexpected error:', err);
    return {
      success: false,
      error: 'An unexpected error occurred. Please try again.'
    };
  }
};

const DiscountSection = ({ productSubtotal, currentPercentage, onDiscountApplied, onDiscountCleared }) => {
  const [codeInput, setCodeInput] = useState(currentPercentage > 0 ? `${currentPercentage}%` : '');
  const [feedback, setFeedback] = useState(null);
  const [feedbackType, setFeedbackType] = useState(null);

  useEffect(() => {
    setCodeInput(currentPercentage > 0 ? `${currentPercentage}%` : '');

    if (currentPercentage > 0) {
      setFeedback(`Discount applied (${currentPercentage}%)`);
      setFeedbackType('success');
    } else {
      setFeedback(null);
      setFeedbackType(null);
    }
  }, [currentPercentage]);

  const discountAmount = useMemo(() => {
    if (!currentPercentage || currentPercentage <= 0) return 0;
    return productSubtotal * (currentPercentage / 100);
  }, [currentPercentage, productSubtotal]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalized = codeInput.trim().toUpperCase();

    // Check if this is the K-Fair discount code that requires validation
    if (normalized === 'KFAIR42781') {
      // Show loading state
      setFeedback('Checking code status...');
      setFeedbackType(null);

      // First, check if the user already used this discount code
      const statusCheck = await checkDiscountStatus();

      if (statusCheck.error) {
        setFeedback(statusCheck.error);
        setFeedbackType('error');
        return;
      }

      if (statusCheck.already_used) {
        // Code already used - apply it without calling validation (no email sent)
        console.log('✅ [Discount] Code already used, applying discount without validation');
        onDiscountApplied(statusCheck.discount_percentage);
        setFeedback(`Discount applied (${statusCheck.discount_percentage}%)`);
        setFeedbackType('success');
        return;
      }

      // Code not yet used - validate it (will mark as used in database)
      console.log('🔍 [Discount] Code not used yet, validating...');
      setFeedback('Validating code...');

      const result = await validateAndApplyDiscountCode(codeInput);

      if (!result.success) {
        setFeedback(result.error);
        setFeedbackType('error');
        return;
      }

      // Success - apply the discount
      onDiscountApplied(result.discount_percentage);
      setFeedback(`Discount applied (${result.discount_percentage}%)`);
      setFeedbackType('success');
      return;
    }

    // For other codes, use the simple percentage resolution
    const percentage = resolveDiscountPercentage(codeInput);

    if (percentage === null) {
      setFeedback('Discount code not recognized');
      setFeedbackType('error');
      return;
    }

    onDiscountApplied(percentage);
    setFeedback(`Discount applied (${percentage}%)`);
    setFeedbackType('success');
  };

  const handleClear = () => {
    setCodeInput('');
    setFeedback(null);
    setFeedbackType(null);
    onDiscountCleared();
  };

  return (
    <DiscountContainer>
      <Title>Discount Code</Title>

      <DiscountForm onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder="Enter discount code"
          value={codeInput}
          onChange={(event) => setCodeInput(event.target.value)}
          $error={feedbackType === 'error'}
          readOnly={currentPercentage > 0}
        />

        <ApplyButton type="submit" disabled={currentPercentage > 0 || !codeInput.trim()}>
          Apply
        </ApplyButton>
      </DiscountForm>

      {feedback && <Feedback $type={feedbackType}>{feedback}</Feedback>}

      {currentPercentage > 0 && (
        <LockIndicator>
          <LockIcon />
          <span>Discount locked - remove to change</span>
        </LockIndicator>
      )}

      {(currentPercentage > 0 || discountAmount > 0) && (
        <>
          <DiscountSummary>
            <span>Applied discount</span>
            <strong>- {formatCurrency(discountAmount)}</strong>
          </DiscountSummary>
          <ClearButton type="button" onClick={handleClear}>
            Remove discount
          </ClearButton>
        </>
      )}
    </DiscountContainer>
  );
};

export default DiscountSection;
